import React, { useState } from "react";
import { ChevronDown, Search, Zap, ShieldCheck } from "lucide-react";

const STEPS = [
  {
    icon: Search,
    title: "Search any name",
    body: "Type a word or brand and we check it instantly across hundreds of extensions — .com, .net, .io, .ai and many more.",
  },
  {
    icon: Zap,
    title: "See live availability",
    body: "Results marked “live” are checked in real time against the official domain registry (RDAP), so you know what's genuinely free.",
  },
  {
    icon: ShieldCheck,
    title: "Compare & register",
    body: "Compare first-year and renewal prices across trusted registrars, then register with the provider you choose — no lock-in.",
  },
];

const FAQS = [
  {
    q: "Is ezidomains.info free to use?",
    a: "Yes. Searching for domains, comparing prices, saving favourites and running bulk checks are all completely free. You only pay a registrar if you decide to register a domain.",
  },
  {
    q: "How accurate are the availability results?",
    a: "Domains showing a “live” badge are checked in real time against the authoritative registry using the public RDAP protocol. A small number of country-code extensions can’t always be checked live and are shown as a best estimate — always confirm at the registrar before buying.",
  },
  {
    q: "Do you sell or register domains directly?",
    a: "No. We’re a search and comparison tool. When you’re ready, we hand you off to a trusted registrar (Ezi Webb, Namecheap or GoDaddy) to complete the registration. You keep full control of your choice and payment.",
  },
  {
    q: "Why do prices differ between registrars?",
    a: "Each registrar sets its own first-year promo and ongoing renewal rates. We show indicative prices and highlight the best ongoing value; the registrar always confirms the final price at checkout.",
  },
  {
    q: "What is a domain hack?",
    a: "A domain hack cleverly uses the extension as part of the word — like del.icio.us or goo.gl. We surface these automatically as you type so you can find short, memorable names.",
  },
  {
    q: "Can I check several names at once?",
    a: "Yes. Switch to Bulk mode and paste up to 10 names to check their .com availability in one go, then open any name to see all extensions.",
  },
];

function FaqItem({ item, open, onToggle, index }) {
  return (
    <div className="border-b border-white/5">
      <button
        onClick={onToggle}
        data-testid={`faq-toggle-${index}`}
        className="flex w-full items-center justify-between gap-4 py-4 text-left transition-colors hover:text-white"
      >
        <span className="text-sm font-medium text-zinc-200">{item.q}</span>
        <ChevronDown
          className={`h-4 w-4 shrink-0 text-zinc-500 transition-transform ${
            open ? "rotate-180" : ""
          }`}
        />
      </button>
      {open && <p className="pb-4 text-sm leading-relaxed text-zinc-500">{item.a}</p>}
    </div>
  );
}

export default function FaqSection() {
  const [open, setOpen] = useState(0);

  return (
    <section className="mt-20 pb-8" data-testid="about-faq-section">
      <div className="mb-12">
        <h2 className="text-center text-lg font-semibold text-white">How it works</h2>
        <div className="mt-8 grid gap-6 sm:grid-cols-3">
          {STEPS.map((s) => (
            <div
              key={s.title}
              className="rounded-xl border border-white/10 bg-white/[0.02] p-5"
            >
              <s.icon className="h-5 w-5 text-emerald-400" />
              <h3 className="mt-3 text-sm font-semibold text-zinc-100">{s.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-zinc-500">{s.body}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="mx-auto max-w-2xl">
        <h2 className="mb-2 text-center text-lg font-semibold text-white">
          About ezidomains.info
        </h2>
        <p className="mb-10 text-center text-sm leading-relaxed text-zinc-500">
          ezidomains.info is a fast, no-nonsense domain search built for founders,
          developers and small businesses. Check availability across hundreds of
          extensions in real time, compare registrar pricing transparently, and save a
          shortlist as you go — then register with whichever provider suits you best.
        </p>

        <h2 className="mb-4 text-center text-lg font-semibold text-white">
          Frequently asked questions
        </h2>
        <div className="rounded-xl border border-white/10 bg-[#0e0e10] px-5">
          {FAQS.map((item, i) => (
            <FaqItem
              key={item.q}
              item={item}
              index={i}
              open={open === i}
              onToggle={() => setOpen((cur) => (cur === i ? null : i))}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
